'use client';
import { useEffect, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

import { UpgradeAndConsentDialog } from '@components/UpgradeAndConsentDialog/V2/UpgradeAndConsentDialog';
import { isWithinLast10Minutes } from '@modules/date-time';

import { useMarketingConsentDetailsQuery } from './actor-marketing-consent-details.graphql';

// Controls upgrade and marketing consent for new users
export const UpgradeAndConsentFlow: React.FC<{
  disableAutoOpen?: boolean;
}> = ({ disableAutoOpen }) => {
  const [showDialog, setShowDialog] = useState(false);
  const autoShowDialogType = useShouldAutoShowDialog(disableAutoOpen);

  useEffect(() => {
    const hasCompletedFlow = sessionStorage.getItem('hasSeenUpgradeFlow') === 'true';

    // Show upgrade / consent dialog automatically
    if (autoShowDialogType && !hasCompletedFlow) {
      // Allow autoShowDialogType to be set before showing the dialog, so animations are run after mount, not at the same time
      const timeout = setTimeout(() => {
        setShowDialog(true);
        sessionStorage.setItem('hasSeenUpgradeFlow', 'true');
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [autoShowDialogType]);

  return (
    <UpgradeAndConsentDialog
      type={autoShowDialogType}
      show={showDialog}
      trackingSource="new_user_auto_trigger"
      onClose={() => {
        setShowDialog(false);
      }}
    />
  );
};

// Page whitelist for auto-show
const homeRegex = /^\/([^/]+)\/home$/;
const cardsRegex = /^\/([^/]+)\/cards/;
const accessoriesRegex = /^\/([^/]+)\/accessories/;

const whitelistMatchers = [homeRegex, cardsRegex, accessoriesRegex];

// this hook determines if the upgrade and consent dialog should be shown automatically, and if so, which type
const useShouldAutoShowDialog = (
  disableAutoOpen?: boolean,
  delay?: number
): 'both' | 'upgrade' | 'consent' | undefined => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [shouldCheck, setShouldCheck] = useState(false);

  const { loading: marketingDetailsLoading, data } = useMarketingConsentDetailsQuery({
    variables: {
      timezone,
    },
    skip: !shouldCheck,
  });

  // Determine if the current URL is in the whitelist
  const isWhitelistUrl = whitelistMatchers.some((matcher) => matcher.test(pathname as string));

  useEffect(() => {
    if (isWhitelistUrl) {
      const timer = setTimeout(() => {
        setShouldCheck(true);
      }, delay || 2000);

      return () => clearTimeout(timer);
    }
  }, [isWhitelistUrl, delay]);

  // Wait for everything to load before deciding if it should appear automatically
  if (!shouldCheck || marketingDetailsLoading || !data || disableAutoOpen) return undefined;

  if (searchParams?.get('openInstruction')) return undefined;

  const isNewUser = isWithinLast10Minutes(data?.actor?.user?.created_at);
  const isFreeUser = data?.actor?.plan.type === 'FREE';
  const isGdprCountry = Boolean(data?.actor?.user?.is_gdpr_country);
  const hasMarketingConsentValue = Boolean(data?.actor?.user?.preferences?.email_marketing?.value);

  // This ensures we only show to fresh users, who aren't already paying
  const shouldAutoShowUpgradeDialog = isFreeUser && isNewUser;
  const shouldAutoShowConsentDialog = isGdprCountry && !hasMarketingConsentValue;

  if (shouldAutoShowUpgradeDialog && shouldAutoShowConsentDialog) return 'both';
  if (shouldAutoShowUpgradeDialog) return 'upgrade';
  if (shouldAutoShowConsentDialog) return 'consent';
  return undefined;
};
